import { type ListingItem } from '@smg-real-estate/hg-search-libs';
import type { ActionContext, ActionTree, GetterTree, Module, Mutation, MutationTree } from 'vuex';

import { type FavouritesListItemNoteUpdatePayload } from '@/store/modules/favourites';
import { type FavouritesAppState } from '@/store/types';
import type { FavouritesList, FavouritesListItem } from '@/types';

export interface FavouriteListingDescriptor {
  savedOn: number;
  listingId: string;
  platform?: string;
  text: string;
}

export enum FavouritesModuleStatus {
  UNINITIALIZED,
  INITIALIZED,
  FETCHING_FAVOURITES,
  FAVOURITES_FETCHED,
}

export interface FavouritesState {
  isFavouritesEnabled: boolean;
  isUserAuthenticated: boolean;
  favouritesModuleStatus: FavouritesModuleStatus;
  isFavouritesOnboardingMessageClosed: boolean;
  hasUnavailableListings: boolean;
  favouriteLoadingListingIds: string[];
  favouriteListingDescriptors: FavouriteListingDescriptor[];
  unavailableListingIds: string[];
  lastRemovedListingDescriptor: FavouriteListingDescriptor | null;
  favouriteListingRecords: ListingItem[];
  favouritesList: FavouritesList | null;
  persistedNotes: FavouritesListItem[];
  features: string[] | null;
}

export interface FavouritesGetters extends GetterTree<FavouritesState, FavouritesAppState> {
  isFavouritesInitialized: (state: FavouritesState) => boolean;
  isUserAuthenticated: (state: FavouritesState) => boolean;
  hasFetchedFavourites: (state: FavouritesState) => boolean;
  favouriteListingRecords: (state: FavouritesState) => ListingItem[];
  isListingFavourite: (state: FavouritesState) => (listingId: string) => boolean;
  isFavouriteLoading: (state: FavouritesState) => (listingId: string | null | undefined) => boolean;
  unavailableListingsCount: (state: FavouritesState) => number;
  unavailableListingsIds: (state: FavouritesState) => string[];
  favouritesListId: (state: FavouritesState) => string | null;
  persistedNoteByListingId: (
    state: FavouritesState,
  ) => (listingId: string) => FavouritesListItem | null;
  features: (state: FavouritesState) => string[];
}

export interface FavouritesMutations extends MutationTree<FavouritesState> {
  setFavouritesModuleStatus: Mutation<FavouritesState>;
  setIsFavouritesEnabled: Mutation<FavouritesState>;
  setIsUserAuthenticated: Mutation<FavouritesState>;
  setIsFavouritesOnboardingMessageClosed: Mutation<FavouritesState>;
  setFavouriteListingDescriptors: Mutation<FavouritesState>;
  addFavouriteLoadingListingId: Mutation<FavouritesState>;
  removeFavouriteLoadingListingId: Mutation<FavouritesState>;
  clearFavouriteLoadingListingIds: Mutation<FavouritesState>;
  setFavouriteListingRecords: Mutation<FavouritesState>;
  setLastRemovedListingDescriptor: Mutation<FavouritesState>;
  setUnavailableListingIds: Mutation<FavouritesState>;
  setPersistedNotes: Mutation<FavouritesState>;
  setFavouritesList: Mutation<FavouritesState>;
  setEnabledFeatures: Mutation<FavouritesState>;
  setHasUnavailableListings: Mutation<FavouritesState>;
}

export interface FavouritesActions extends ActionTree<FavouritesState, FavouritesAppState> {
  removeFavourite: (
    actionContext: ActionContext<FavouritesState, FavouritesAppState>,
    listingId: string,
  ) => Promise<void>;
  fetchFavouriteList: (
    actionContext: ActionContext<FavouritesState, FavouritesAppState>,
  ) => Promise<FavouritesList | null>;
  fetchFavouriteListingRecords: (
    actionContext: ActionContext<FavouritesState, FavouritesAppState>,
  ) => void;
  closeOnboardingMessage: (
    actionContext: ActionContext<FavouritesState, FavouritesAppState>,
  ) => void;
  restoreLastRemovedFavourite: (
    actionContext: ActionContext<FavouritesState, FavouritesAppState>,
  ) => Promise<void>;
  removeUnavailableFavourites: (
    actionContext: ActionContext<FavouritesState, FavouritesAppState>,
  ) => void;
  fetchFavouriteListingDescriptors: (
    actionContext: ActionContext<FavouritesState, FavouritesAppState>,
  ) => Promise<void>;
  persistNoteForAuthUser: (
    actionContext: ActionContext<FavouritesState, FavouritesAppState>,
    payload: FavouritesListItemNoteUpdatePayload,
  ) => Promise<void>;
  dismissUnavailableListingsBanner: (
    actionContext: ActionContext<FavouritesState, FavouritesAppState>,
  ) => Promise<boolean>;
}

export interface FavouritesModule extends Module<FavouritesState, FavouritesAppState> {
  state: () => FavouritesState;
  getters: FavouritesGetters;
  actions: FavouritesActions;
  mutations: FavouritesMutations;
}
